import * as React from "react";
import {
  Alert,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Link,
  MenuItem,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { supabase } from "../logic/client";
import { useI18n } from "../i18n/I18nProvider";
import { canUseControlPanel } from "../logic/cache";


type Step = "email" | "login" | "set-password" | "lab-register" | "forgot" | "confirm-email" | "loading";

const EDGE_REQUEST = "request-invite";
const EDGE_LINK = "link-employee";
const EDGE_LAB_REGISTER = "lab-register";
const EDGE_CHECK_EMAIL_DOMAIN = "check-email-domain";
const EDGE_SEND_EMAIL_CONFIRMATION = "send-email-confirmation";
const AWAITING_EMAIL_CONFIRMATION_KEY = "tk.awaitingEmailConfirmation";

type RequestInviteResponse =
  | { ok: false; reason: "not_found" | string }
  | {
      ok: true;
      mode: "login" | "set_password";
      availability_submitted_at: string | null;
      employee_id: string;
    };

type LinkEmployeeResponse =
  | { ok: false; reason?: string }
  | { ok: true; employee_id: string; availability_submitted_at: string | null };

type SelfRegisterResponse = { ok: true; employee_id: string } | { ok: false; reason?: string };

const DEPARTMENT_OPTIONS = [
  "Management / Executive",
  "Operations",
  "Production / Technical",
  "Facility / Security",
  "HR / People",
  "Communications",
  "Logistics / Supply Chain",
  "Finance / Admin",
  "Other",
];

export default function Entry(props: { handlePageChange: (page: string) => void }) {
  const { handlePageChange } = props;
  const { t } = useI18n();

  const [step, setStep] = React.useState<Step>("email");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [companyName, setCompanyName] = React.useState("");
  const [department, setDepartment] = React.useState("");
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [position, setPosition] = React.useState("");
  const [personalAddress, setPersonalAddress] = React.useState("");
  
  const [legalAccepted, setLegalAccepted] = React.useState(false);

  const [forgotEmail, setForgotEmail] = React.useState("");
  const [forgotInfo, setForgotInfo] = React.useState<string | null>(null);

  const [error, setError] = React.useState<string | null>(null);
  const [pendingConfirmationUserId, setPendingConfirmationUserId] = React.useState<string | null>(null);

  const emailNorm = email.trim().toLowerCase();

  const redirectAfterAuth = async (employeeId: string) => {
    const { data } = await supabase
      .from("employees")
      .select("company_role")
      .eq("id", employeeId)
      .maybeSingle();
    
    const role = data?.company_role;

    if (canUseControlPanel(role)) {
      handlePageChange("ControlPanel");
      return;
    }
    handlePageChange("JourneyPillars");
  };

  const requestInvite = async (): Promise<RequestInviteResponse> => {
    const { data, error } = await supabase.functions.invoke(EDGE_REQUEST, {
      body: { email: emailNorm },
    });
    if (error) throw new Error(error.message || "Failed to call request-invite.");
    return data as RequestInviteResponse;
  };

  const registerSelf = async (authUserId: string): Promise<SelfRegisterResponse> => {
    const { data, error } = await supabase.functions.invoke(EDGE_LAB_REGISTER, {
      body: {
        email: emailNorm,
        auth_user_id: authUserId,
        company_name: companyName.trim(),
        department: department.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        position: position.trim(),
        personal_address: personalAddress.trim(),
        anonymous: false,
      },
    });
    if (error) throw new Error(error.message || "Failed to call lab-register.");
    //console.log("[Entry] lab-register result", data);
    return data as SelfRegisterResponse;
  };

  const linkEmployee = async (): Promise<LinkEmployeeResponse> => {
    const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
    if (sessionErr) throw new Error(sessionErr.message);

    const token = sessionData.session?.access_token;
    if (!token) throw new Error("No session token found (login required).");

    //console.log("[Entry] invoking link-employee", { emailNorm });
    const { data, error } = await supabase.functions.invoke(EDGE_LINK, {
      body: { email: emailNorm },
      headers: { Authorization: `Bearer ${token}` },
    });

    if (error) throw new Error(error.message || "Failed to link employee.");
    //console.log("[Entry] link-employee result", data);
    return data as LinkEmployeeResponse;
  };

  const linkAfterAuth = async () => {
    const linked = await linkEmployee();
    if (!linked.ok) throw new Error(linked.reason || "Failed to link employee.");
    await redirectAfterAuth(linked.employee_id);
  };

  const sendManualEmailConfirmation = async (userId: string) => {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    const token = session?.access_token;

    if (!token) {
      throw new Error("No session token found for email confirmation.");
    }

    const { error: sendErr } = await supabase.functions.invoke(
      EDGE_SEND_EMAIL_CONFIRMATION,
      {
        body: {
          email: emailNorm,
          user_id: userId,
          origin: window.location.origin,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (sendErr) {
      throw new Error(sendErr.message || "Failed to send confirmation email.");
    }
  };

  const hasConfirmedEmail = async (userId: string): Promise<boolean> => {
    const { data, error } = await supabase
      .from("email_confirmations")
      .select("id")
      .eq("user_id", userId)
      .eq("email", emailNorm)
      .not("confirmed_at", "is", null)
      .limit(1);

    if (error) throw error;
    return Array.isArray(data) && data.length > 0;
  };

  const waitForEmailConfirmation = async (userId: string) => {
    localStorage.setItem(AWAITING_EMAIL_CONFIRMATION_KEY, "1");
    localStorage.setItem("tk.currentPage", "Entry");
    setPendingConfirmationUserId(userId);
    setError(null);
    setPassword("");
    setShowPassword(false);
    setStep("confirm-email");
    await supabase.auth.signOut();
  };

  /* ---------------- STEP 1: EMAIL SUBMIT ---------------- */

  const submitEmail = async () => {
    setError(null);

    if (!emailNorm || !emailNorm.includes("@")) {
      setError(t("entry.err.invalidEmail"));
      return;
    }

    setStep("loading");

    try {
      const res = await requestInvite();

      if (!res.ok) {
        setError(t("entry.err.notFound"));
        setStep("email");
        return;
      }

      setPassword("");
      setShowPassword(false);
      setStep(res.mode === "login" ? "login" : "set-password");
    } catch (e: any) {
      setError(e?.message ?? t("entry.err.edgeInvoke"));
      setStep("email");
    }
  };

  /* ---------------- FORGOT PASSWORD ---------------- */

  const openForgot = () => {
    setError(null);
    setForgotInfo(null);
    setForgotEmail(emailNorm || "");
    setPassword("");
    setShowPassword(false);
    setStep("forgot");
  };

  const sendResetEmail = async () => {
    setError(null);
    setForgotInfo(null);

    const e = forgotEmail.trim().toLowerCase();
    if (!e || !e.includes("@")) {
      setForgotInfo(t("entry.reset.invalidEmail"));
      return;
    }

    const { data, error } = await supabase.functions.invoke("send-password-reset", {
      body: { email: e, origin: window.location.origin },
    });

    if (error || !data?.ok) {
      // still don’t reveal if email exists
      setForgotInfo(t("entry.reset.sent"));
      return;
    }

    setForgotInfo(t("entry.reset.sent"));
  };

  /* ---------------- STEP 2A: LOGIN ---------------- */

  const login = async () => {
    setError(null);

    if (!password) {
      setError(t("entry.err.noPassword"));
      return;
    }

    setStep("loading");

    const { data, error: signInError } = await supabase.auth.signInWithPassword({
      email: emailNorm,
      password,
    });

    if (signInError) {
      setError(signInError.message);
      setStep("login");
      return;
    }

    if (!data.user?.id) {
      setError(t("entry.err.edgeInvoke"));
      setStep("login");
      return;
    }

    try {
      const confirmed = await hasConfirmedEmail(data.user.id);
      if (!confirmed) {
        await waitForEmailConfirmation(data.user.id);
        return;
      }
      await linkAfterAuth();
    } catch (e: any) {
      setError(e?.message ?? t("entry.err.linkFailed"));
      setStep("login");
    }
  };

  /* ---------------- STEP 2B: SIGNUP (SET PASSWORD) ---------------- */

  const setInitialPassword = async () => {
    setError(null);

    if (!password || password.length < 8) {
      setError(t("entry.err.pwTooShort"));
      return;
    }

    setStep("loading");

    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email: emailNorm,
      password,
    });

    //console.log("[Entry] signUpError", signUpError);
    //console.log("[Entry] signUpData", signUpData);

    if (signUpError) {
      const msg = (signUpError.message || "").toLowerCase();
      if (
        msg.includes("already registered") ||
        msg.includes("already exists") ||
        msg.includes("database error saving new user")
      ) {
        // user exists -> go login (do NOT try to link yet because we aren't authenticated)
        setError(null);
        setPassword("");
        setShowPassword(false);
        setStep("login");
        return;
      }

      setError(signUpError.message);
      setStep("set-password");
      return;
    }

    if (!signUpData.user?.id) {
      setError("Failed to create account.");
      setStep("set-password");
      return;
    }

    try {
      await sendManualEmailConfirmation(signUpData.user.id);
      await waitForEmailConfirmation(signUpData.user.id);
    } catch (e: any) {
      setError(e?.message ?? "Account created, but confirmation email failed.");
      setStep("set-password");
    }
  };

  /* ---------------- SELF REGISTRATION ---------------- */

  const submitSelfRegistration = async () => {
    setError(null);

    if (!emailNorm || !emailNorm.includes("@")) {
      setError(t("entry.err.invalidEmail"));
      return;
    }

    if (!password || password.length < 8) {
      setError(t("entry.err.pwTooShort"));
      return;
    }

    if (!companyName.trim()) {
      setError(t("entry.err.companyRequired"));
      return;
    }

    if (!department.trim()) {
      setError(t("entry.err.departmentRequired"));
      return;
    }

    if (!firstName.trim() || !lastName.trim()) {
      setError(t("entry.err.nameRequired"));
      return;
    }

    if (!position.trim()) {
      setError(t("entry.err.positionRequired"));
      return;
    }

    if (!personalAddress.trim()) {
      setError(t("entry.err.personalAddressRequired"));
      return;
    }

    if (!legalAccepted) {
      setError(t("entry.err.legalRequired"));
      return;
    }

    setStep("loading");

    try {
      const { data: domainCheck, error: domainCheckError } = await supabase.functions.invoke<{ ok: boolean }>(
        EDGE_CHECK_EMAIL_DOMAIN,
        { body: { email: emailNorm } }
      );

      // Fail open on a broken check itself (network hiccup, function
      // outage) -- only a definitive "no" blocks registration.
      if (!domainCheckError && domainCheck && domainCheck.ok === false) {
        setError(t("entry.err.invalidEmailDomain"));
        setStep("lab-register");
        return;
      }

      // 1. create auth user first
      let { data: signUpData, error: signUpError } =
        await supabase.auth.signUp({
          email: emailNorm,
          password,
      });

      if (signUpError) {
        const msg = (signUpError.message || "").toLowerCase();
        if (msg.includes("already")) {
          // The auth account exists, but that alone doesn't mean
          // registration ever finished -- a previous attempt may have
          // created the auth user and then failed before lab-register
          // created the employee row, leaving a dead end: signup says
          // "already registered", but login would say "no account found".
          // If these are the same credentials, signing in recovers a
          // session so we can just finish registration now.
          const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
            email: emailNorm,
            password,
          });

          if (signInError || !signInData.user?.id) {
            window.alert(t("entry.alert.userExists"));
            setStep("login");
            return;
          }

          signUpData = signInData;
          signUpError = null;
        } else {
          setError(signUpError.message);
          setStep("lab-register");
          return;
        }
      }

      if (!signUpData?.user?.id) {
        setError(t("entry.err.selfRegisterFailed"));
        setStep("lab-register");
        return;
      }

      // 2. create domain data
      const res = await registerSelf(signUpData.user.id);
      if (!res.ok) {
        if (res.reason === "employee_exists") {
          // The recovered sign-in turned out to belong to a fully
          // registered account after all -- send them to the normal login
          // flow instead of showing a raw backend reason code as an error.
          await supabase.auth.signOut();
          setPassword("");
          setShowPassword(false);
          setStep("login");
          return;
        }

        setError(
          res.reason === "invalid_email_domain"
            ? t("entry.err.invalidEmailDomain")
            : res.reason ?? t("entry.err.selfRegisterFailed")
        );
        setStep("lab-register");
        return;
      }

      // 3. send confirmation
      await sendManualEmailConfirmation(signUpData.user.id);

      // 4. sign out and wait for confirmation
      await waitForEmailConfirmation(signUpData.user.id);
    } catch (e: any) {
      setError(e?.message ?? t("entry.err.selfRegisterFailed"));
      setStep("lab-register");
    }
  };

  return (
  <Box
    sx={{
      width: "100%",
      minHeight: "100vh",
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
      px: { xs: 2, sm: 3 },
      py: { xs: 10, sm: 4 },
    }}
  >
    {/* BACKGROUND */}
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        backgroundImage: "url(/background.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        zIndex: 0,
      }}
    />

    {/* DARK OVERLAY */}
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        background:
          "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.75) 60%, rgba(0,0,0,0.92) 100%)",
        zIndex: 1,
      }}
    />

    {/* HEADER */}
    <Box
      sx={{
        position: "absolute",
        top: { xs: 20, sm: 60 },
        width: "100%",
        textAlign: "center",
        zIndex: 2,
        color: "#fff",
        px: 2,
      }}
    >
      <Typography sx={{ fontWeight: 900, fontSize: { xs: "1.5rem", sm: "2.125rem" } }}>
        takeKONTROL
      </Typography>

      <Typography sx={{ mt: 1, color: "#d0d0d0", fontSize: { xs: "0.85rem", sm: "1rem" } }}>
        Stärke beginnt mit Klarheit
      </Typography>
    </Box>

    {/* PANEL */}
    <Paper
  elevation={10}
  sx={{
    zIndex: 3,
    width: "100%",
    maxWidth: 640,
    borderRadius: 3,
    p: { xs: 3, md: 5 },
    background: "rgba(18,18,18,0.88)",
    backdropFilter: "blur(6px)",
    border: "1px solid rgba(255,255,255,0.08)",
    boxShadow: "0 30px 90px rgba(0,0,0,0.8)",

    maxHeight: "75vh",        // prevents overflow
    overflowY: "auto",        // scroll inside panel
  }}
>
      <Stack spacing={3} sx={{ pb: 1 }}>

        {error && (
          <Alert severity="error" variant="filled" onClose={() => setError(null)} sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {/* EMAIL STEP */}
        {step === "email" && (
          <>
            <Typography sx={{ color: "#ffffff" }}>
              {t("entry.email.title")}
            </Typography>

            <TextField
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("entry.email.placeholder")}
              fullWidth
              autoFocus
              sx={{
                "& .MuiInputBase-root": {
                  color: "#f1f1f1",
                  backgroundColor: "rgba(255,255,255,0.06)",
                },
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitEmail();
              }}
            />

            <Button
              variant="contained"
              onClick={submitEmail}
              sx={{
                height: 50,
                fontWeight: 700,
                backgroundColor: "#b71c1c",
                "&:hover": { backgroundColor: "#d32f2f" },
              }}
            >
              {t("entry.email.continue")}
            </Button>

            <Button
              variant="text"
              onClick={() => {
                setError(null);
                setPassword("");
                setShowPassword(false);
                setStep("lab-register");
              }}
              sx={{
                textTransform: "none",
                color: "#ff6b6b",
                fontWeight: 600,
              }}
            >
              {t("entry.email.register")}
            </Button>
          </>
        )}

        {/* LOGIN STEP */}
        {step === "login" && (
          <>
            <Typography sx={{ color: "#ffffff" }}>
              {t("entry.password.login.title")}
            </Typography>

            <TextField
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              autoFocus
              sx={{
                "& .MuiInputBase-root": {
                  color: "#f1f1f1",
                  backgroundColor: "rgba(255,255,255,0.06)",
                },
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") login();
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={() => setShowPassword((s) => !s)}
                    >
                      {showPassword ? (
                        <VisibilityOffOutlinedIcon />
                      ) : (
                        <VisibilityOutlinedIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              variant="contained"
              onClick={login}
              sx={{
                height: 50,
                fontWeight: 700,
                backgroundColor: "#b71c1c",
                "&:hover": { backgroundColor: "#d32f2f" },
              }}
            >
              {t("entry.password.login.cta")}
            </Button>

            <Stack spacing={0.5} alignItems="center">
              <Button
                variant="text"
                onClick={openForgot}
                sx={{ textTransform: "none", color: "#ff6b6b" }}
              >
                {t("entry.password.forgot")}
              </Button>

              <Button
                variant="text"
                onClick={() => {
                  setError(null);
                  setPassword("");
                  setShowPassword(false);
                  setStep("email");
                }}
                sx={{ textTransform: "none", color: "#bbbbbb" }}
              >
                {t("entry.password.useDifferent")}
              </Button>
            </Stack>
          </>
        )}

        {/* SET PASSWORD */}
        {step === "set-password" && (
          <>
            <Typography sx={{ color: "#ffffff" }}>
              {t("entry.password.set.title")}
            </Typography>

            <TextField
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              autoFocus
              sx={{
                "& .MuiInputBase-root": {
                  color: "#f1f1f1",
                  backgroundColor: "rgba(255,255,255,0.06)",
                },
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") setInitialPassword();
              }}
            />

            <Button
              variant="contained"
              onClick={setInitialPassword}
              sx={{
                height: 50,
                fontWeight: 700,
                backgroundColor: "#b71c1c",
                "&:hover": { backgroundColor: "#d32f2f" },
              }}
            >
              {t("entry.password.set.cta")}
            </Button>

            <Button
              variant="text"
              onClick={() => setStep("email")}
              sx={{ textTransform: "none", color: "#ff6b6b" }}
            >
              {t("entry.back")}
            </Button>
          </>
        )}

        {/* SELF REGISTER */}
        {step === "lab-register" && (
          <>
            <Typography sx={{ color: "#ffffff" }}>
              {t("entry.register.title")}
            </Typography>

            <TextField
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("entry.email.placeholder")}
              fullWidth
              sx={{
                "& .MuiInputBase-root": {
                  color: "#f1f1f1",
                  backgroundColor: "rgba(255,255,255,0.06)",
                },
              }}
            />

            <TextField
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("entry.password.placeholder")}
              fullWidth
              sx={{
                "& .MuiInputBase-root": {
                  color: "#f1f1f1",
                  backgroundColor: "rgba(255,255,255,0.06)",
                },
              }}
            />

            <TextField
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder={t("entry.register.companyPlaceholder")}
              fullWidth
              sx={{
                "& .MuiInputBase-root": {
                  color: "#f1f1f1",
                  backgroundColor: "rgba(255,255,255,0.06)",
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "rgba(255,255,255,0.7)",
                  opacity: 1,
                },
              }}
            />

            <TextField
              select
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              placeholder={t("entry.register.departmentPlaceholder")}
              fullWidth
              SelectProps={{
                displayEmpty: true,
                renderValue: (value) =>
                  value ? String(value) : t("entry.register.departmentPlaceholder"),
              }}
              sx={{
                "& .MuiInputBase-root": {
                  color: "#f1f1f1",
                  backgroundColor: "rgba(255,255,255,0.06)",
                },
                "& .MuiSelect-icon": {
                  color: "#f1f1f1",
                },
              }}
            >
              <MenuItem value="" disabled>
                {t("entry.register.departmentPlaceholder")}
              </MenuItem>
              {DEPARTMENT_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder={t("entry.register.firstName")}
              fullWidth
              sx={{
                "& .MuiInputBase-root": {
                  color: "#f1f1f1",
                  backgroundColor: "rgba(255,255,255,0.06)",
                },
              }}
            />
            <TextField
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder={t("entry.register.lastName")}
              fullWidth
              sx={{
                "& .MuiInputBase-root": {
                  color: "#f1f1f1",
                  backgroundColor: "rgba(255,255,255,0.06)",
                },
              }}
            />
            <TextField
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              placeholder={t("entry.register.positionPlaceholder")}
              fullWidth
              sx={{
                "& .MuiInputBase-root": {
                  color: "#f1f1f1",
                  backgroundColor: "rgba(255,255,255,0.06)",
                },
              }}
            />
            <TextField
              value={personalAddress}
              onChange={(e) => setPersonalAddress(e.target.value)}
              placeholder={t("entry.register.personalAddress")}
              fullWidth
              sx={{
                "& .MuiInputBase-root": {
                  color: "#f1f1f1",
                  backgroundColor: "rgba(255,255,255,0.06)",
                },
              }}
            />

              <FormControlLabel
                sx={{
                  alignItems: "flex-start",
                  color: "#e0e0e0",
                  mx: 0,
                  "& .MuiFormControlLabel-label": {
                    fontSize: 14,
                    lineHeight: 1.45,
                  },
                }}
                control={
                  <Checkbox
                    checked={legalAccepted}
                    onChange={(e) => setLegalAccepted(e.target.checked)}
                    sx={{
                      color: "rgba(255,255,255,0.55)",
                      p: 0,
                      mr: 1.25,
                      mt: 0.1,
                      "&.Mui-checked": { color: "#d32f2f" },
                    }}
                  />
                }
                label={
                  <Typography component="span" sx={{ color: "#e0e0e0", fontSize: 14 }}>
                    {t("entry.register.consentPrefix")}
                    <Link
                      component="button"
                      type="button"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        handlePageChange("Privacy");
                      }}
                      sx={{
                        color: "#ffffff",
                        fontWeight: 800,
                        textDecorationColor: "rgba(255,255,255,0.55)",
                        verticalAlign: "baseline",
                      }}
                    >
                      {t("entry.register.privacyPolicy")}
                    </Link>
                    {t("entry.register.consentJoin")}
                    <Link
                      component="button"
                      type="button"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        handlePageChange("Terms");
                      }}
                      sx={{
                        color: "#ffffff",
                        fontWeight: 800,
                        textDecorationColor: "rgba(255,255,255,0.55)",
                        verticalAlign: "baseline",
                      }}
                    >
                      {t("entry.register.termsConditions")}
                    </Link>
                    .
                  </Typography>
                }
              />

            <Button
              variant="contained"
              onClick={submitSelfRegistration}
              disabled={!legalAccepted}
              sx={{
                height: 50,
                fontWeight: 700,
                backgroundColor: "#b71c1c",
                "&.Mui-disabled": {
                  color: "rgba(255,255,255,0.45)",
                  backgroundColor: "rgba(183,28,28,0.38)",
                },
                "&:hover": { backgroundColor: "#d32f2f" },
              }}
            >
              {t("entry.register.cta")}
            </Button>

            <Button
              variant="text"
              onClick={() => {
                  setError(null);
                  setPassword("");
                  setShowPassword(false);
                  setCompanyName("");
                  setLegalAccepted(false);
                  setStep("email");
                }}
              sx={{ textTransform: "none", color: "#ff6b6b" }}
            >
              {t("entry.back")}
            </Button>
          </>
        )}

        {/* FORGOT PASSWORD */}
        {step === "forgot" && (
          <>
            <Typography sx={{ color: "#ffffff" }}>
              {t("entry.reset.subtitle")}
            </Typography>

            <TextField
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              placeholder="Company email"
              fullWidth
              autoFocus
              onKeyDown={(e) => {
                  if (e.key === "Enter") sendResetEmail();
                }}
              sx={{
                "& .MuiInputBase-root": {
                  color: "#f1f1f1",
                  backgroundColor: "rgba(255,255,255,0.06)",
                },
              }}
            />

            <Button
              variant="contained"
              onClick={sendResetEmail}
              sx={{
                height: 50,
                fontWeight: 700,
                backgroundColor: "#b71c1c",
                "&:hover": { backgroundColor: "#d32f2f" },
              }}
            >
              {t("entry.reset.cta")}
            </Button>

            {forgotInfo && (
              <Typography sx={{ color: "#bbb" }}>
                {forgotInfo}
              </Typography>
            )}

            <Button
              variant="text"
              onClick={() => {
                  setForgotInfo(null);
                  setError(null);
                  setStep("login");
                }}
              sx={{ textTransform: "none", color: "#ff6b6b" }}
            >
              {t("entry.reset.backToLogin")}
            </Button>
          </>
        )}

        {/* CONFIRM EMAIL */}
        {step === "confirm-email" && (
          <>
            <Typography sx={{ color: "#ffffff" }}>
              {t("entry.confirm.title")}
            </Typography>

            <Typography sx={{ color: "#bbbbbb" }}>
              {t("entry.confirm.subtitle")}
            </Typography>

            <Typography sx={{ color: "#ffffff" }}>
              <strong>{emailNorm}</strong>
            </Typography>

            <Button
              variant="contained"
              sx={{
                height: 50,
                fontWeight: 700,
                backgroundColor: "#b71c1c",
                "&:hover": { backgroundColor: "#d32f2f" },
              }}
              onClick={async () => {
                  setError(null);

                  if (!pendingConfirmationUserId) {
                    setError(t("entry.err.edgeInvoke"));
                    return;
                  }

                  try {
                    await sendManualEmailConfirmation(pendingConfirmationUserId);
                  } catch (e: any) {
                    setError(e?.message ?? t("entry.err.edgeInvoke"));
                  }
                }}
            >
              {t("entry.confirm.resend")}
            </Button>

            <Button
              variant="text"
              onClick={() => {
                setError(null);
                setPassword("");
                setShowPassword(false);
                setStep("login");
              }}
              sx={{ textTransform: "none", color: "#ff6b6b" }}
            >
              {t("entry.confirm.backToLogin")}
            </Button>
          </>
        )}

        {step === "loading" && (
          <Typography sx={{ color: "#ffffff" }}>
            {t("entry.loading")}
          </Typography>
        )}
      </Stack>
    </Paper>
  </Box>
);
}
